import { useState } from 'react';
import { useMissionControlStore } from '../store.js';
import type { Lane, LaneRole } from '../types.js';

interface LaneDetailProps {
  lane: Lane | null;
  siblings: Lane[];
}

export function LaneDetail({ lane, siblings }: LaneDetailProps) {
  const appendLaneLog = useMissionControlStore((s) => s.appendLaneLog);
  const setLaneStatus = useMissionControlStore((s) => s.setLaneStatus);
  const setActiveLane = useMissionControlStore((s) => s.setActiveLane);
  const emitEvent = useMissionControlStore((s) => s.emitEvent);
  const [prompt, setPrompt] = useState('');

  if (!lane) {
    return (
      <section className="panel lane-detail lane-detail-empty" aria-label="Lane detail">
        <div className="section-heading">
          <span className="section-kicker">Lane detail</span>
          <strong>Select a lane to focus</strong>
        </div>
        <p className="sidecar-empty">Click any lane in the grid to inspect logs and send prompts.</p>
      </section>
    );
  }

  function handlePromptLane(targetLaneId: string) {
    if (!lane) return;
    const text = prompt.trim();
    if (!text) return;
    appendLaneLog(targetLaneId, { text: `Prompt: ${text}`, tone: 'neutral' });
    setLaneStatus(targetLaneId, 'running');
    emitEvent({
      workspaceId: lane.workspaceId,
      laneId: targetLaneId,
      kind: targetLaneId === lane.id ? 'lane_progress' : 'lane_handoff',
      summary: targetLaneId === lane.id ? `Re-prompted ${lane.role}` : `Handoff to ${targetLaneId}`,
    });
    setPrompt('');
  }

  function handleKill() {
    if (!lane) return;
    setLaneStatus(lane.id, 'failed');
    appendLaneLog(lane.id, { text: 'Lane killed by operator', tone: 'bad' });
    emitEvent({
      workspaceId: lane.workspaceId,
      laneId: lane.id,
      kind: 'lane_done',
      summary: `${lane.role} lane killed by operator`,
    });
  }

  const handoffTargets: Array<{ id: string; role: LaneRole; name: string }> = siblings
    .filter((sibling) => sibling.id !== lane.id)
    .map((sibling) => ({ id: sibling.id, role: sibling.role, name: sibling.name }));

  return (
    <section className="panel lane-detail" aria-label="Lane detail">
      <div className="section-heading">
        <span className="section-kicker">Lane detail</span>
        <strong>{lane.name}</strong>
      </div>

      <dl className="lane-detail-meta">
        <div>
          <dt>role</dt>
          <dd>{lane.role}</dd>
        </div>
        <div>
          <dt>provider</dt>
          <dd>{lane.provider}</dd>
        </div>
        <div>
          <dt>model</dt>
          <dd>{lane.model}</dd>
        </div>
        <div>
          <dt>status</dt>
          <dd>{lane.status}</dd>
        </div>
      </dl>

      <div className="lane-detail-task">
        <span className="section-kicker">Current task</span>
        <p>{lane.currentTask}</p>
      </div>

      <div className="lane-detail-log" aria-label="Lane log">
        <span className="section-kicker">Log</span>
        {lane.logs.length === 0 ? (
          <p className="lane-log-empty">No log lines yet.</p>
        ) : (
          <ol className="lane-detail-log-list">
            {lane.logs
              .slice()
              .reverse()
              .map((line) => (
                <li key={line.at} className={`lane-log lane-log-${line.tone}`}>
                  <span className="lane-log-time">
                    {new Date(line.at).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </span>
                  <span>{line.text}</span>
                </li>
              ))}
          </ol>
        )}
      </div>

      <div className="lane-detail-prompt">
        <label>
          <span>Send a prompt to this lane</span>
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="Take the failing test output and propose a minimal patch."
            rows={2}
            aria-label="Lane prompt"
          />
        </label>
        <div className="lane-detail-actions">
          <button
            type="button"
            className="primary-button"
            disabled={!prompt.trim()}
            onClick={() => handlePromptLane(lane.id)}
            aria-label={`Send prompt to ${lane.name}`}
          >
            Send to this lane
          </button>
          <button type="button" className="ghost-button" onClick={() => setActiveLane(null)}>
            Close
          </button>
          <button
            type="button"
            className="danger-button"
            onClick={handleKill}
            aria-label={`Kill ${lane.name}`}
          >
            Kill lane
          </button>
        </div>

        {handoffTargets.length > 0 ? (
          <div className="lane-detail-handoff">
            <span className="section-kicker">Handoff to</span>
            <div className="lane-detail-handoff-list">
              {handoffTargets.map((target) => (
                <button
                  key={target.id}
                  type="button"
                  className="ghost-button"
                  disabled={!prompt.trim()}
                  onClick={() => handlePromptLane(target.id)}
                  aria-label={`Handoff to ${target.name}`}
                >
                  {target.role}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
